import React, { useEffect, useState, useMemo, useRef } from 'react';
import './wellTasks.scss';
import { RootState } from '../../store';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { useParams } from 'react-router-dom';
import Loader from '../../features/common/Loader/Loader';
import { fetchTasksDataAsync } from '../slices/TasksHistorySlice';
import TaskHistoryTable from '../data-tables/TaskHistoryTable';
import TableHeader from '../data-tables/TableHeader';
import ExportTable from '../common/export-table/ExportTable';
import FilterSelector from '../filter-modal/FilterSelector';
import { ReactComponent as FilterIcon } from '../../images/filter.svg';
import { useRBAC } from '../../rbac/RbacProvider';
import { R, DASHBOARDS, WIDGETS } from '../../rbac/resource';
import { ProtectComponent } from '../../rbac';
import { formatDate } from '../../utilities/dateHelpers';

const WellTasks: React.FC = () => {
  const dispatch = useAppDispatch();
  const { assetId } = useParams<{ assetId: string }>();

  const tasksData = useAppSelector((state: RootState) => state.TasksHistorySlice.data);
  const loading = useAppSelector((state: RootState) => state.TasksHistorySlice.loading);
  const selectedAssetId = useAppSelector((state) => state.assetGroups?.selectedAssetId);

  const widgetName = 'System log';

  const { can } = useRBAC();
  const resource = R.widget(DASHBOARDS.NOTES_HISTORY, WIDGETS.NOTE_HISTORY);
  const rPermission = {
    canExport: can(resource, 'export'),
  };

  // Table data for export
  const [tableRows, setTableRows] = useState<any[]>([]);

  // Filter state - persist across page refreshes using localStorage
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [selectedNoteTypes, setSelectedNoteTypes] = useState<string[]>(() => {
    const storageKey = `WellTaskFilter_${assetId}`;
    const saved = localStorage.getItem(storageKey);
    return saved ? JSON.parse(saved) : [];
  });

  // Track previous assetId to only reset filter when it actually changes
  const prevAssetIdRef = useRef<string | undefined>(undefined);

  // Get unique note types for filter
  const uniqueNoteTypes = useMemo(() => {
    const types = new Set<string>();
    (tasksData || []).forEach((item) => {
      if (item.noteSource && item.noteSource.trim()) {
        types.add(item.noteSource);
      }
    });
    return Array.from(types).sort();
  }, [tasksData]);

  // Filter data based on selected note types
  const filteredTasksData = useMemo(() => {
    if (selectedNoteTypes.length === 0) {
      return tasksData || [];
    }
    return (tasksData || []).filter((item) => selectedNoteTypes.includes(item.noteSource));
  }, [tasksData, selectedNoteTypes]);

  // Handle filter apply
  const handleFilterApply = (selectedTypes: string[]) => {
    setSelectedNoteTypes(selectedTypes);
    // Persist to localStorage for this specific assetId
    const storageKey = `WellTaskFilter_${assetId}`;
    localStorage.setItem(storageKey, JSON.stringify(selectedTypes));
  };

  useEffect(() => {
    if (assetId) {
      dispatch(
        fetchTasksDataAsync({
          assetId: assetId,
          startDate: undefined, //'06/01/2023 8:19:33 PM',
          endDate: undefined, //'06/06/2025 8:19:33 PM',
          filterBy: undefined,
          filterValue: undefined,
          orderBy: undefined,
          orderColumn: undefined,
          appendData: false,
        }),
      );
    }
  }, [dispatch, assetId]);

  // Reset filter when assetId changes (but not on initial load/refresh)
  useEffect(() => {
    if (prevAssetIdRef.current !== undefined && prevAssetIdRef.current !== assetId) {
      setSelectedNoteTypes([]);
      const storageKey = `WellTaskFilter_${assetId}`;
      localStorage.removeItem(storageKey);
    }
    prevAssetIdRef.current = assetId;
  }, [assetId]);

  // Handle table data ready callback
  const handleTableDataReady = (rows: any[]) => {
    setTableRows(rows);
  };

  // Define export columns configuration
  const exportColumns = [
    {
      key: 'noteDate',
      header: 'Date',
      accessor: (row: any) => formatDate(row.values.noteDate, true) || '',
    },
    {
      key: 'noteDescription',
      header: 'Event',
      accessor: (row: any) => row.values.noteDescription || '',
    },
    {
      key: 'noteSource',
      header: 'Type',
      accessor: (row: any) => row.values.noteSource || '',
    },
    {
      key: 'userId',
      header: 'User id',
      accessor: (row: any) => row.values.userId || '',
    },
  ];

  return (
    <ProtectComponent resource={resource} action='view' widgetName={widgetName}>
      <div className='well-tasks-container'>
        <main className='main-container table-wrap'>
          <TableHeader title={widgetName} />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              margin: '16px 20px',
            }}
          >
            <ExportTable
              data={tableRows}
              fileName={`System-Log-${selectedAssetId}`}
              sheetName='System log'
              columns={exportColumns}
              disabled={loading || !tableRows || tableRows.length === 0}
              rPermission={rPermission}
            />
            <button
              className={`flex h-[40px] pt-[10px] pr-[16px] pb-[10px] pl-[16px] gap-[8px] justify-center items-center shrink-0 flex-nowrap rounded-[8px] border-solid border relative overflow-hidden shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] table-button ${loading
                  ? 'bg-[#2a2a2a] border-[#3a3a3a] cursor-not-allowed opacity-50'
                  : 'bg-[#131f2e] border-[#4a5463] hover:bg-[#1c2a3b] cursor-pointer'
                }`}
              onClick={() => !loading && setIsFilterPanelOpen(true)}
              disabled={loading}
            >
              <FilterIcon width='16' height='16' />
              <span className='text-[14px] leading-[20px] text-[#f7f9f9] text-left whitespace-nowrap'>Filter</span>
              {selectedNoteTypes.length > 0 && selectedNoteTypes.length < uniqueNoteTypes.length && (
                <span className='ml-1 px-2 py-1 text-xs bg-[#00789e] text-white rounded-full'>
                  {selectedNoteTypes.length}
                </span>
              )}
            </button>
          </div>
          {loading ? (
            <div className='task-loading-state'>
              <Loader />
            </div>
          ) : (
            <div className='task-table-wrapper'>
              <TaskHistoryTable taskHistoryItems={filteredTasksData} onDataReady={handleTableDataReady} />
            </div>
          )}
        </main>
        {/* Filter Selector Panel */}
        {isFilterPanelOpen && (
          <FilterSelector
            noteTypes={uniqueNoteTypes}
            selectedTypes={selectedNoteTypes}
            onUpdateSelectedTypes={setSelectedNoteTypes}
            onClose={() => setIsFilterPanelOpen(false)}
            onApply={handleFilterApply}
          />
        )}
      </div>
    </ProtectComponent>
  );
};

export default WellTasks;
